// MealPlan.js — Orchestrator component for displaying meal plans to review.
// State, effects, and handlers live in extracted hooks; UI in extracted components.
import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMealPlan } from "../../contexts/MealPlanContext";
import { useUser } from "../../contexts/UserContext";
import { useDrawerState } from "../../hooks/useDrawerState";
import { TOTAL_EXPECTED_RECIPES } from "../../hooks/useMealPlanGeneration";
import { useReasoningDisplay, ReasoningPanel } from "../../utils/reasoningUtils";
import { abortProgressiveProcessor } from "../../utils/progressiveRecipeProcessor";

// Extracted hooks
import { useGroceryList } from "./hooks/useGroceryList";
import { useMealPlanLoader } from "./hooks/useMealPlanLoader";
import { useRecipeActions } from "./hooks/useRecipeActions";
import { useMealPlanSave } from "./hooks/useMealPlanSave";

// Extracted utils
import { computeTotalFromProducts, computeAdjustedTotalFromProducts, computeRecipeCostsFromProducts, getDisplayCost } from "./utils/mealPlanHelpers";
import { getCountryCurrency } from "../../services/countryService";

// Extracted components
import MealPlanHeader from "./components/MealPlanHeader";
import MealSection from "./components/MealSection";
import GroceryListCard from "./components/GroceryListCard";
import NotHappyCard from "./components/NotHappyCard";
import StickyBottomBar from "./components/StickyBottomBar";
import { shouldShowIntro, markIntroShown, INTRO_DURATION_MS } from "./components/ReviewMealsIntro";

// Existing components
import ProductList from "../ProductList/ProductList";
import RecipeDrawer from "../Recipes/RecipeDrawer";
import LoadingProgressBar from "./LoadingProgressBar";
import RecipeAlternativesDrawer from "../Recipes/RecipeAlternativesDrawer";
import RemoveAllConfirmationDrawer from "./RemoveAllConfirmationDrawer";
import RemoveRecipeConfirmationDrawer from "../Recipes/RemoveRecipeConfirmationDrawer";
import MealPlanDetailsDrawer from "../Member/Drawers/MealPlanDetailsDrawer";
import WhyNineMealsDrawer from "../UserWizard/Drawers/WhyNineMealsDrawer";
import TriesLeftDrawer from "../PremiumPromo/TriesLeftDrawer";
import ReplaceMealPlanDrawer from "./ReplaceMealPlanDrawer";
import MyMealPlans from "../Member/MyMealPlans";
import LoginModal from "../LoginModal/LoginModal";
import { useFavoriteMeals } from "../../hooks/useFavoriteMeals";
import backendClient from "../../services/backendClient";
import { findMatchedProduct } from "../../utils/recipeIngredientMatching";

/**
 * MealPlan component — orchestrates hooks and renders the meal plan review page.
 * @param {Function} onNavigateToWizard - Callback to navigate back to the wizard
 * @param {Function} onSaveMealPlan - Callback to handle navigation after saving meal plan
 */
function MealPlan({ onNavigateToWizard, onSaveMealPlan }) {
  const location = useLocation();
  const isFromWizard = location.state?.fromWizard === true;
  const isFromWizardRef = useRef(isFromWizard);
  if (isFromWizard) isFromWizardRef.current = true;

  const { t } = useTranslation('mealplan');

  // ─── Contexts ─────────────────────────────────────────────
  const mealPlanCtx = useMealPlan();
  const {
    mealPlan, setMealPlan,
    totalCost, setTotalCost,
    productList, setProductList,
    maxStorageTime, setMaxStorageTime,
    adjustedTotalCost, setAdjustedTotalCost,
    haveItState, setHaveItState,
    leftovers, setLeftovers,
    myProducts, setMyProducts,
    resetMealPlan,
    startStreaming, updateStreamProgress, endStreaming,
  } = mealPlanCtx;

  const userCtx = useUser();
  const { user, budget, numberOfPeople, selectedCuisine, dietaryPreferences, cookingStyle } = userCtx;

  // ─── Drawer state ─────────────────────────────────────────
  const drawers = useDrawerState();
  const {
    drawerOpen, setDrawerOpen,
    selectedRecipe, setSelectedRecipe,
    sidebarOpen, setSidebarOpen,
    alternativesDrawerOpen, dislikedRecipe,
    alternativeRecipes, setAlternativeRecipes,
    setDislikedRecipe, setAlternativesDrawerOpen,
    removeAllDrawerOpen, mealTypeToRemove,
    setMealTypeToRemove, setRemoveAllDrawerOpen,
    removeRecipeDrawerOpen, recipeToRemove,
    setRemoveRecipeDrawerOpen, setRecipeToRemove,
    detailsDrawerOpen, setDetailsDrawerOpen,
    freshUpDrawerOpen, setFreshUpDrawerOpen,
    triesLeftDrawerOpen, setTriesLeftDrawerOpen,
    replaceMealPlanDrawerOpen, setReplaceMealPlanDrawerOpen,
    isLoginModalOpen, setIsLoginModalOpen,
  } = drawers;

  // ─── Reasoning ────────────────────────────────────────────
  const {
    reasoningSteps, showReasoning,
    addReasoningStep, clearReasoningSteps, toggleReasoningDisplay,
  } = useReasoningDisplay();

  // ─── Local state ──────────────────────────────────────────
  const [imagesLoading, setImagesLoading] = useState({});
  const [promoLoadingByRecipeKey, setPromoLoadingByRecipeKey] = useState({});
  const [promoAddedByRecipeKey, setPromoAddedByRecipeKey] = useState({});
  const [promoToast, setPromoToast] = useState({ visible: false, message: '', kind: 'success' });

  const showPromoToast = useCallback((message, kind = 'success') => {
    setPromoToast({ visible: true, message, kind });
  }, []);

  useEffect(() => {
    if (!promoToast.visible) return undefined;
    const timer = setTimeout(() => {
      setPromoToast((prev) => ({ ...prev, visible: false }));
    }, 2400);
    return () => clearTimeout(timer);
  }, [promoToast.visible]);

  // Shared mounted ref — passed to hooks for async cancellation
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      console.log('[MealPlan] Component unmounting - aborting pending operations');
      isMountedRef.current = false;
      abortProgressiveProcessor();
    };
  }, []);

  // Per-recipe prematch cache shared between useMealPlanLoader (writes during stream)
  // and useGroceryList (reads at final grocery generation). Map<normalizedIngredientName,
  // {productId, productName, category, product, ...}>. Lets the final grocery pipeline
  // reuse Pass 1 hits from per-recipe matching and only run Pass 2 + AI fallback for the
  // genuinely unresolved subset. Reset by the loader at the start of each generation
  // attempt.
  const prematchCacheRef = useRef(new Map());

  // Scroll refs for meal sections
  const breakfastScrollRef = useRef(null);
  const lunchScrollRef = useRef(null);
  const dinnerScrollRef = useRef(null);

  // ─── Grocery list hook ────────────────────────────────────
  const grocery = useGroceryList({
    numberOfPeople,
    budget,
    setMealPlan,
    setProductList,
    setTotalCost,
    setAdjustedTotalCost,
    setLeftovers,
    updateStreamProgress,
    addReasoningStep,
    isMountedRef,
    prematchCacheRef,
  });

  const {
    groceryListLoading, setGroceryListLoading,
    groceryOperationInProgress, setGroceryOperationInProgress,
    processingStatus,
    userCountry,
    generateGroceryList,
  } = grocery;

  // ─── Loader hook ──────────────────────────────────────────
  const loader = useMealPlanLoader({
    isFromWizard,
    isFromWizardRef,
    mealPlanCtx: {
      mealPlan, setMealPlan,
      totalCost, setTotalCost,
      productList, setProductList,
      setMaxStorageTime,
      adjustedTotalCost, setAdjustedTotalCost,
      haveItState, setHaveItState,
      leftovers, setLeftovers,
      resetMealPlan, maxStorageTime,
      startStreaming, updateStreamProgress, endStreaming,
    },
    userCtx: { user, budget, numberOfPeople, selectedCuisine, dietaryPreferences, cookingStyle },
    generateGroceryList,
    addReasoningStep,
    clearReasoningSteps,
    userCountry,
    setImagesLoading,
    setGroceryListLoading,
    isMountedRef,
    onNavigateToWizard,
    t,
    prematchCacheRef,
  });

  const {
    loadingState, loadingPhase, loadingRecipesReady,
    wasLoadedFromDb, setWasLoadedFromDb,
    restoredFromLocalStorage,
    isPremium, isAdmin,
    triesLeft,
    replacementsRemaining,
    maxReplacements,
    currentDescriptionIndex, rotatingDescriptions,
    handleReset,
  } = loader;

  // ─── Loader staged exit (waits for Lottie loop boundary) ──
  // 1. loadingState leaves "initial" → pendingExitRef.current = true (but keep loader visible)
  // 2. DotLottie fires `loop` event at natural boundary → handleLoopBoundary sets loaderExiting=true
  // 3. After 300ms fade-out → unmount loader
  // 4. Main content becomes visible (gated on !loaderVisible)
  // Fallback: if Lottie doesn't fire loop within 5s, force exit.
  const LOADER_EXIT_MS = 300;
  const LOADER_EXIT_FALLBACK_MS = 5000;
  const [loaderMounted, setLoaderMounted] = useState(loadingState === "initial");
  const [loaderExiting, setLoaderExiting] = useState(false);
  const pendingExitRef = useRef(false);
  const fallbackTimerRef = useRef(null);

  const beginLoaderExit = useCallback(() => {
    setLoaderExiting(true);
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    setTimeout(() => setLoaderMounted(false), LOADER_EXIT_MS);
  }, []);

  const handleLoopBoundary = useCallback(() => {
    if (pendingExitRef.current && !loaderExiting) {
      pendingExitRef.current = false;
      beginLoaderExit();
    }
  }, [loaderExiting, beginLoaderExit]);

  useEffect(() => {
    if (loadingState === "initial") {
      setLoaderMounted(true);
      setLoaderExiting(false);
      pendingExitRef.current = false;
      return undefined;
    }
    if (!loaderMounted || loaderExiting) return undefined;
    pendingExitRef.current = true;
    fallbackTimerRef.current = setTimeout(() => {
      if (pendingExitRef.current) {
        pendingExitRef.current = false;
        beginLoaderExit();
      }
    }, LOADER_EXIT_FALLBACK_MS);
    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [loadingState, loaderMounted, loaderExiting, beginLoaderExit]);

  const loaderVisible = loadingState === "initial" || loaderMounted;

  // ─── Review intro onboarding (fullscreen yellow overlay, once per session) ───
  const [introActive, setIntroActive] = useState(() => shouldShowIntro());
  const mainContentVisible =
    !loaderVisible && loadingState !== "checking" && loadingState !== "error";
  useEffect(() => {
    if (!introActive || !mainContentVisible) return undefined;
    markIntroShown();
    const timer = setTimeout(() => setIntroActive(false), INTRO_DURATION_MS);
    return () => clearTimeout(timer);
  }, [introActive, mainContentVisible]);

  // ─── Country-driven grocery effects ───────────────────────
  useEffect(() => {
    grocery.setupCountryEffect(mealPlan, productList, restoredFromLocalStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCountry]);

  useEffect(() => {
    grocery.regenerateAfterRestore(restoredFromLocalStorage, mealPlan, productList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restoredFromLocalStorage, userCountry]);

  // ─── Keep selectedRecipe in sync with mealPlan updates ────
  useEffect(() => {
    if (selectedRecipe && drawerOpen) {
      // BUG #4 fix: also match by originalTitle in case AI changed the title during streaming
      const updated = mealPlan.find(r =>
        r.title === selectedRecipe.title ||
        r.originalTitle === selectedRecipe.title ||
        r.title === selectedRecipe.originalTitle
      );
      if (updated && updated !== selectedRecipe) {
        if (updated.imageURL !== selectedRecipe.imageURL ||
            updated.instructions !== selectedRecipe.instructions ||
            updated.description !== selectedRecipe.description) {
          setSelectedRecipe(updated);
        }
      }
    }
  }, [mealPlan, selectedRecipe, drawerOpen, setSelectedRecipe]);

  // ─── Recipe actions hook ──────────────────────────────────
  const actions = useRecipeActions({
    mealPlan, setMealPlan,
    productList, setProductList,
    leftovers, setLeftovers,
    setTotalCost, setAdjustedTotalCost,
    budget, numberOfPeople,
    userCountry, cookingStyle,
    dietaryPreferences, selectedCuisine,
    generateGroceryList,
    addReasoningStep,
    groceryOperationInProgress,
    setGroceryOperationInProgress,
    setImagesLoading,
    isPremium,
    onNavigateToWizard,
    drawerActions: {
      setDrawerOpen, setSelectedRecipe,
      setDislikedRecipe, dislikedRecipe,
      setAlternativeRecipes,
      setAlternativesDrawerOpen,
      setRemoveAllDrawerOpen, setMealTypeToRemove,
      setRemoveRecipeDrawerOpen, setRecipeToRemove,
      setTriesLeftDrawerOpen,
    },
  });

  const {
    isGeneratingAlternatives, isRemovingAll, isRemovingRecipe,
    handleRecipeClick, handleDislikeRecipe,
    handleGenerateMoreAlternatives,
    handleSelectAlternative, handleRemoveRecipe,
    handleConfirmRemoveRecipe, handleConfirmRemoveAll,
    handleRemoveAllClick,
    handleCloseRemoveAllDrawer, handleCloseRemoveRecipeDrawer,
    handleCloseDrawer,
  } = actions;

  // ─── Save / auth hook ─────────────────────────────────────
  const save = useMealPlanSave({
    mealPlan, setMealPlan,
    productList, setProductList,
    totalCost, setTotalCost,
    adjustedTotalCost, setAdjustedTotalCost,
    haveItState, setHaveItState,
    leftovers, setLeftovers,
    myProducts, setMyProducts,
    cookingStyle,
    isFromWizard,
    isFromWizardRef,
    restoredFromLocalStorage,
    setWasLoadedFromDb,
    setIsLoginModalOpen,
    setReplaceMealPlanDrawerOpen,
    onSaveMealPlan,
  });

  const {
    isSavingMealPlan, isReplacingMealPlan,
    handleSaveMealPlan, handleLoginSuccess,
    handleConfirmReplaceMealPlan, handleCancelReplaceMealPlan,
  } = save;

  // ─── Favorites ────────────────────────────────────────────
  const { isMealFavorite, toggleFavoriteMeal, favoriteMeals } = useFavoriteMeals();

  // ─── Derived values ───────────────────────────────────────
  const computedTotal = useMemo(() => computeTotalFromProducts(productList), [productList]);

  const liveAdjustedCost = useMemo(() => {
    const adjusted = computeAdjustedTotalFromProducts(productList, haveItState);
    if (adjusted === null) return null;
    const currencySymbol = userCountry ? getCountryCurrency(userCountry).symbol : "€";
    return `${currencySymbol}${adjusted.toFixed(2)}`;
  }, [productList, haveItState, userCountry]);

  const displayCost = useMemo(
    () => getDisplayCost({ adjustedTotalCost: liveAdjustedCost, totalCost, computedTotalFromProducts: computedTotal, userCountry }),
    [liveAdjustedCost, totalCost, computedTotal, userCountry]
  );

  // Consumed-only cost per meal, recomputed live as "I have it" toggles change.
  const currencySymbol = useMemo(
    () => (userCountry ? getCountryCurrency(userCountry).symbol : "€"),
    [userCountry]
  );
  const recipeCostByTitle = useMemo(
    () => computeRecipeCostsFromProducts(productList, haveItState),
    [productList, haveItState]
  );

  const allImagesLoaded = mealPlan.length > 0 &&
    mealPlan.every(r => (
      r.imageURL ||
      r.imageFailed ||
      (!r.loadingStatus && r.instructionsFormatted)
    ) && !imagesLoading[r.title]);

  const allRecipesComplete = mealPlan.length > 0 &&
    mealPlan.every(r => !r.isPlaceholder && r.instructions && !r.fetchingDetailsForImage);

  const groceryListLoaded = productList.length > 0 && !groceryListLoading;

  const canShowControls = groceryListLoaded && allRecipesComplete && allImagesLoaded;

  // ─── Open grocery list sidebar ────────────────────────────
  const handleOpenGroceryList = useCallback(() => {
    setSidebarOpen(true);
    if (productList.length === 0) {
      setGroceryListLoading(true);
    }
  }, [setSidebarOpen, productList.length, setGroceryListLoading]);

  // ─── Wrapping handlers that need dislikedRecipe ───────────
  const onSelectAlternative = useCallback(
    (alt) => handleSelectAlternative(alt, dislikedRecipe),
    [handleSelectAlternative, dislikedRecipe]
  );

  const onConfirmRemoveRecipe = useCallback(
    () => handleConfirmRemoveRecipe(recipeToRemove),
    [handleConfirmRemoveRecipe, recipeToRemove]
  );

  const onConfirmRemoveAll = useCallback(
    () => handleConfirmRemoveAll(mealTypeToRemove),
    [handleConfirmRemoveAll, mealTypeToRemove]
  );

  const handleAddToPromo = useCallback(async (recipe) => {
    if (!recipe || !isAdmin) return;

    const recipeKey = recipe.id || recipe.originalTitle || recipe.title;
    if (!recipeKey) return;

    try {
      setPromoLoadingByRecipeKey((prev) => ({ ...prev, [recipeKey]: true }));

      // Attach the subset of matched grocery products needed by this recipe so
      // the promo landing page can render localized names + product images
      // (same logic as RecipeDrawer). Backend stores the meal payload as-is
      // under sourceRecipe, so embedding _matchedProducts persists them.
      let matchedProducts = [];
      if (Array.isArray(recipe.ingredients) && productList?.length) {
        const seen = new Set();
        for (const ing of recipe.ingredients) {
          const product = findMatchedProduct(ing, productList);
          if (product) {
            const key = product.productId || product.name || product.originalName;
            if (key && !seen.has(key)) {
              seen.add(key);
              matchedProducts.push(product);
            }
          }
        }
      }
      const mealPayload = matchedProducts.length
        ? { ...recipe, _matchedProducts: matchedProducts }
        : recipe;

      const result = await backendClient.post('/api/user/promo-meals', { meal: mealPayload }, { timeout: 10000 });

      if (result?.created) {
        setPromoAddedByRecipeKey((prev) => ({ ...prev, [recipeKey]: true }));
        showPromoToast('Meal added to promo', 'success');
      } else {
        showPromoToast('Meal is already in promo', 'info');
      }
    } catch (error) {
      showPromoToast(error?.message || 'Failed to add meal to promo', 'error');
    } finally {
      setPromoLoadingByRecipeKey((prev) => ({ ...prev, [recipeKey]: false }));
    }
  }, [isAdmin, showPromoToast, productList]);

  // ─── Early return: loaded from DB → show MyMealPlans ─────
  if (wasLoadedFromDb) {
    return (
      <MyMealPlans
        mealPlan={mealPlan}
        productList={productList}
        totalCost={totalCost}
        maxStorageTime={maxStorageTime}
        imagesLoading={imagesLoading}
        leftovers={leftovers}
        savedCookingStyle={cookingStyle}
      />
    );
  }

  // ─── Meal groups ──────────────────────────────────────────
  const breakfasts = mealPlan.filter(r => r.mealType === "Breakfast");
  const lunches = mealPlan.filter(r => r.mealType === "Lunch");
  const dinners = mealPlan.filter(r => r.mealType === "Dinner");

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className="min-h-safe-screen">
      {/* Loading state */}
      {loaderVisible && (
        <LoadingProgressBar
          phase={loadingPhase}
          recipesReady={loadingRecipesReady}
          totalRecipes={TOTAL_EXPECTED_RECIPES}
          isExiting={loaderExiting}
          onLoopBoundary={handleLoopBoundary}
        />
      )}

      {/* Error state */}
      {loadingState === "error" && (
        <div className="flex flex-col items-center justify-center pt-20 pb-10 px-4">
          <div className="bg-white rounded-[24px] p-8 shadow-[0px_4px_24px_rgba(13,12,7,0.08)] max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-[#0D0C07] text-xl font-bold mb-2">{t('errors.loadFailedTitle', 'We could not load your meal plan')}</h3>
            <p className="text-[#65635D] text-base mb-6">
              {t('errors.loadFailedMessage', 'Please try again. If this keeps happening, start a new meal plan.')}
            </p>
            <button
              className="w-full bg-[#FFCB00] text-[#0D0C07] font-bold py-3.5 px-6 rounded-full hover:bg-yellow-400 transition-colors active:scale-[0.98]"
              onClick={handleReset}
            >
              {t('common:buttons.tryAgain', 'Try again')}
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      {!loaderVisible && loadingState !== "checking" && loadingState !== "error" && (
        <>
          <MealPlanHeader
            currentDescription={rotatingDescriptions[currentDescriptionIndex]}
            onOpenDetails={() => setDetailsDrawerOpen(true)}
            onOpenFreshUp={() => setFreshUpDrawerOpen(true)}
            introActive={introActive}
          />

          {/* Meal Sections */}
          <div
            className="min-h-0 mt-2 pt-2
                          bg-[#F8F8F7]
                          rounded-[12px_12px_0px_0px]
                          border-t-[2px]
                          border-t-[#FFF]
                          shadow-[0px_-4px_24px_0px_rgba(13,12,7,0.12)]"
          >
            <div className="pb-32">
              {breakfasts.length > 0 && (
                <MealSection
                  mealType="Breakfast"
                  meals={breakfasts}
                  scrollRef={breakfastScrollRef}
                  cookingStyle={cookingStyle}
                  recipeCostByTitle={recipeCostByTitle}
                  currencySymbol={currencySymbol}
                  canShowControls={canShowControls}
                  groceryOperationInProgress={groceryOperationInProgress}
                  imagesLoading={imagesLoading}
                  onRecipeClick={handleRecipeClick}
                  onDislikeRecipe={handleDislikeRecipe}
                  onRemoveAllClick={handleRemoveAllClick}
                  isGeneratingAlternatives={isGeneratingAlternatives}
                  onToggleFavorite={toggleFavoriteMeal}
                  isMealFavorite={isMealFavorite}
                  isAdmin={isAdmin}
                  onAddToPromo={handleAddToPromo}
                  promoLoadingByRecipeKey={promoLoadingByRecipeKey}
                  promoAddedByRecipeKey={promoAddedByRecipeKey}
                />
              )}
              {lunches.length > 0 && (
                <MealSection
                  mealType="Lunch"
                  meals={lunches}
                  scrollRef={lunchScrollRef}
                  cookingStyle={cookingStyle}
                  recipeCostByTitle={recipeCostByTitle}
                  currencySymbol={currencySymbol}
                  canShowControls={canShowControls}
                  groceryOperationInProgress={groceryOperationInProgress}
                  imagesLoading={imagesLoading}
                  onRecipeClick={handleRecipeClick}
                  onDislikeRecipe={handleDislikeRecipe}
                  onRemoveAllClick={handleRemoveAllClick}
                  isGeneratingAlternatives={isGeneratingAlternatives}
                  onToggleFavorite={toggleFavoriteMeal}
                  isMealFavorite={isMealFavorite}
                  isAdmin={isAdmin}
                  onAddToPromo={handleAddToPromo}
                  promoLoadingByRecipeKey={promoLoadingByRecipeKey}
                  promoAddedByRecipeKey={promoAddedByRecipeKey}
                />
              )}
              {dinners.length > 0 && (
                <MealSection
                  mealType="Dinner"
                  meals={dinners}
                  scrollRef={dinnerScrollRef}
                  cookingStyle={cookingStyle}
                  recipeCostByTitle={recipeCostByTitle}
                  currencySymbol={currencySymbol}
                  canShowControls={canShowControls}
                  groceryOperationInProgress={groceryOperationInProgress}
                  imagesLoading={imagesLoading}
                  onRecipeClick={handleRecipeClick}
                  onDislikeRecipe={handleDislikeRecipe}
                  onRemoveAllClick={handleRemoveAllClick}
                  isGeneratingAlternatives={isGeneratingAlternatives}
                  onToggleFavorite={toggleFavoriteMeal}
                  isMealFavorite={isMealFavorite}
                  isAdmin={isAdmin}
                  onAddToPromo={handleAddToPromo}
                  promoLoadingByRecipeKey={promoLoadingByRecipeKey}
                  promoAddedByRecipeKey={promoAddedByRecipeKey}
                />
              )}

              <GroceryListCard
                groceryListLoading={groceryListLoading}
                productList={productList}
                displayCost={displayCost}
                onOpenGroceryList={handleOpenGroceryList}
              />

              <NotHappyCard
                isPremium={isPremium}
                onNavigateToWizard={onNavigateToWizard}
                onOpenTriesLeft={() => setTriesLeftDrawerOpen(true)}
              />
            </div>
          </div>

          {/* Grocery sidebar */}
          {sidebarOpen && (
            <ProductList
              products={Array.isArray(productList) ? productList : []}
              onClose={() => setSidebarOpen(false)}
              loading={groceryListLoading}
              processingStatus={processingStatus}
              leftovers={Array.isArray(leftovers) ? leftovers : []}
              country={userCountry}
              isAdmin={isAdmin}
            />
          )}

          {/* Drawers */}
          <RecipeDrawer
            selectedRecipe={selectedRecipe}
            isOpen={drawerOpen}
            onClose={handleCloseDrawer}
            imagesLoading={imagesLoading}
            onDislikeRecipe={handleDislikeRecipe}
            isGeneratingAlternatives={isGeneratingAlternatives}
            dislikedRecipe={dislikedRecipe}
            numberOfPeople={numberOfPeople}
            cookingStyle={cookingStyle}
            groceryListLoaded={canShowControls && !groceryOperationInProgress}
            showPromoButton={isAdmin && !promoAddedByRecipeKey[selectedRecipe?.id || selectedRecipe?.originalTitle || selectedRecipe?.title]}
            onAddToPromo={handleAddToPromo}
            isAddingToPromo={!!promoLoadingByRecipeKey[selectedRecipe?.id || selectedRecipe?.originalTitle || selectedRecipe?.title]}
          />

          <ReasoningPanel
            reasoningSteps={reasoningSteps}
            visible={showReasoning}
            onClose={toggleReasoningDisplay}
          />

          <RecipeAlternativesDrawer
            isOpen={alternativesDrawerOpen}
            onClose={() => {
              setAlternativesDrawerOpen(false);
              setDislikedRecipe(null);
              setAlternativeRecipes([]);
            }}
            dislikedRecipe={dislikedRecipe}
            alternativeRecipes={alternativeRecipes}
            favoriteMeals={favoriteMeals}
            onSelectAlternative={onSelectAlternative}
            onRemoveRecipe={handleRemoveRecipe}
            onGenerateMore={handleGenerateMoreAlternatives}
            isLoading={isGeneratingAlternatives}
            isSubmitting={false}
            isPremium={isPremium}
            replacementsRemaining={replacementsRemaining}
            maxReplacements={maxReplacements}
          />

          <RemoveAllConfirmationDrawer
            isOpen={removeAllDrawerOpen}
            onClose={handleCloseRemoveAllDrawer}
            mealType={mealTypeToRemove}
            onConfirmRemove={onConfirmRemoveAll}
            isLoading={isRemovingAll}
          />

          <RemoveRecipeConfirmationDrawer
            isOpen={removeRecipeDrawerOpen}
            onClose={handleCloseRemoveRecipeDrawer}
            recipe={recipeToRemove}
            onConfirmRemove={onConfirmRemoveRecipe}
            isLoading={isRemovingRecipe}
          />

          <MealPlanDetailsDrawer
            isOpen={detailsDrawerOpen}
            onClose={() => setDetailsDrawerOpen(false)}
            onTryAgain={() => {
              setDetailsDrawerOpen(false);
              if (isPremium) {
                onNavigateToWizard();
              } else {
                setTriesLeftDrawerOpen(true);
              }
            }}
            isPremium={isPremium}
          />

          <WhyNineMealsDrawer
            isOpen={freshUpDrawerOpen}
            onClose={() => setFreshUpDrawerOpen(false)}
          />

          <TriesLeftDrawer
            isOpen={triesLeftDrawerOpen}
            onClose={() => setTriesLeftDrawerOpen(false)}
            onTryAgain={() => {
              setTriesLeftDrawerOpen(false);
              handleReset();
            }}
            isRegistered={!!user}
          />

          <ReplaceMealPlanDrawer
            isOpen={replaceMealPlanDrawerOpen}
            onClose={handleCancelReplaceMealPlan}
            onConfirmReplace={handleConfirmReplaceMealPlan}
            isLoading={isReplacingMealPlan}
            triesLeft={triesLeft}
            isPremium={isPremium}
          />
        </>
      )}

      {/* Login Modal — always rendered */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Sticky Bottom Bar */}
      {!loaderVisible && loadingState !== "checking" && loadingState !== "error" && (
        <StickyBottomBar
          canShowControls={canShowControls}
          groceryListLoading={groceryListLoading}
          productList={productList}
          adjustedTotalCost={liveAdjustedCost}
          totalCost={totalCost}
          computedTotalFromProducts={computedTotal}
          userCountry={userCountry}
          isSavingMealPlan={isSavingMealPlan}
          onOpenGroceryList={handleOpenGroceryList}
          onSaveMealPlan={handleSaveMealPlan}
          introActive={introActive}
        />
      )}

      {promoToast.visible && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[90px] z-[260] px-4 py-2 rounded-[10px] shadow-[0px_8px_24px_rgba(13,12,7,0.18)] text-[13px] font-semibold"
          style={{
            backgroundColor:
              promoToast.kind === 'error'
                ? '#FEE2E2'
                : promoToast.kind === 'info'
                  ? '#DBEAFE'
                  : '#DCFCE7',
            color:
              promoToast.kind === 'error'
                ? '#991B1B'
                : promoToast.kind === 'info'
                  ? '#1E3A8A'
                  : '#166534',
          }}
        >
          {promoToast.message}
        </div>
      )}
    </div>
  );
}

export default MealPlan;
